import type { SerializedIconRegistry } from './registries/IconRegistry';
import type { SerializedMediaRegistry } from './registries/MediaRegistry';
import type { SerializedMusicRegistry } from './registries/MusicRegistry';
import type { SerializedVoiceoverRegistry } from './registries/VoiceoverRegistry';
import { isToolMessage, type ConversationMessage, type ToolDefinition } from './types';

import { ApiService } from 'js/src/client';
import type { ConversationDetail } from 'js/src/client';

/**
 * Typed, recursive projection of `ConversationDetail`: its JSON fields given real
 * types, plus a `children` subtree reconstructed from tool-message references.
 */
export type ConversationData = Pick<
    ConversationDetail,
    'id' | 'model' | 'task_type' | 'video_id'
> & {
    messages: ConversationMessage[];
    messages_hash?: string | null;
    tools?: ToolDefinition[];
    media_registry?: SerializedMediaRegistry | null;
    music_registry?: SerializedMusicRegistry | null;
    voiceover_registry?: SerializedVoiceoverRegistry | null;
    icon_registry?: SerializedIconRegistry | null;
    children?: ConversationData[];
};

function collectChildConversationIds(messages: ConversationMessage[]): string[] {
    const childIds: string[] = [];
    messages.forEach((message) => {
        if (isToolMessage(message) && message.child_conversation_ids) {
            childIds.push(...message.child_conversation_ids);
        }
    });
    return childIds;
}

/**
 * Build the nested tree from an already-fetched detail, recursing into its child
 * subtree. A child that fails to fetch is omitted rather than failing the whole load.
 */
async function fetchConversationTree(detail: ConversationDetail): Promise<ConversationData> {
    const messages = detail.messages as unknown as ConversationMessage[];

    const childIds = collectChildConversationIds(messages);
    const childResults = await Promise.all(
        childIds.map(async (childId) => {
            try {
                return await fetchConversationData(childId);
            } catch (err) {
                // eslint-disable-next-line no-console
                console.warn(`Failed to load child conversation ${childId}:`, err);
                return null;
            }
        }),
    );
    const children = childResults.filter((child): child is ConversationData => child !== null);

    return {
        id: detail.id,
        messages,
        messages_hash: detail.messages_hash,
        model: detail.model,
        tools: detail.tools as ToolDefinition[],
        video_id: detail.video_id,
        task_type: detail.task_type,
        media_registry: detail.media_registry as SerializedMediaRegistry | null,
        music_registry: detail.music_registry as SerializedMusicRegistry | null,
        voiceover_registry: detail.voiceover_registry as SerializedVoiceoverRegistry | null,
        icon_registry: detail.icon_registry as SerializedIconRegistry | null,
        ...(children.length > 0 && { children }),
    };
}

/** Fetch a conversation and its full child subtree. */
export async function fetchConversationData(conversationId: string): Promise<ConversationData> {
    const response = await ApiService.getConversation({
        path: { id: conversationId },
    });

    if (!response.data) {
        throw new Error('Conversation not found');
    }

    return fetchConversationTree(response.data);
}

/**
 * Fetch the most recent conversation for a video and its full child subtree,
 * scoped to a task type when given so a video with conversations from several tasks
 * resumes the matching one.
 */
export async function fetchLatestConversationData(
    videoId: number,
    taskType?: string,
): Promise<ConversationData | null> {
    const response = await ApiService.getLatestConversationForVideo({
        path: { video_id: String(videoId) },
        query: taskType ? { task_type: taskType } : undefined,
    });
    if (!response.data || !response.data.messages) {
        return null;
    }
    return fetchConversationTree(response.data);
}
